import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/core';
import { debounce } from 'lodash';
import {
  SearchField,
  SpinningLoader,
  Text,
  useControlledField,
} from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';

import { WhitelistedLocation, WhitelistedLocationsReply } from '../../../sync-app.types';

const SEARCH_DEBOUNCE_MS = 150;

type Props = {
  isOpen: boolean;
  isLoading: boolean;
  data?: WhitelistedLocationsReply;
};

/**
 * Renders the reply from `RPCAdapter.WhitelistedLocations`. Every PMS
 * location is listed; each row carries its whitelist status. When the
 * adapter has no whitelist configured, `syncing_all` is true and a banner is
 * shown to explain that every location is currently syncing.
 */
export const ViewLocationsPanel = ({ isOpen, isLoading, data }: Props) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchInput,
    onChange: (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
  });

  const indexed = useMemo<IndexedLocation[]>(
    () =>
      (data?.locations ?? []).map((loc) => ({
        loc,
        searchBlob: `${loc.id} ${loc.name} ${loc.address}`.toLowerCase(),
      })),
    [data]
  );

  const whitelistedCount = useMemo(
    () => indexed.reduce((acc, { loc }) => acc + (loc.whitelisted ? 1 : 0), 0),
    [indexed]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return indexed;
    return indexed.filter((item) => item.searchBlob.includes(term));
  }, [indexed, searchTerm]);

  if (!isOpen) return null;

  const hasData = !!data;
  const showLoader = isLoading && !hasData;
  const totalLocations = indexed.length;

  return (
    <div css={panelStyle}>
      <div css={panelHeaderStyle}>
        <div css={panelTitleStyle}>
          <Text as="span" weight="bold" size="medium">
            Whitelisted Locations
          </Text>
          {hasData && (
            <Text as="span" size="small" color="light">
              {whitelistedCount} whitelisted
            </Text>
          )}
        </div>
      </div>

      {showLoader ? (
        <div css={panelLoadingStyle}>
          <SpinningLoader size="small" />
          <Text as="span" size="small" color="light">
            Fetching locations…
          </Text>
        </div>
      ) : !hasData ? null : totalLocations === 0 ? (
        <Text color="light" css={panelEmptyStyle}>
          No locations returned by the adapter.
        </Text>
      ) : (
        <>
          <div css={panelSearchStyle}>
            <SearchField
              {...searchFieldProps}
              name="view-locations__search"
              placeholder="Search by name, ID, or address"
              autoComplete="off"
            />
          </div>
          <div css={panelListStyle}>
            {filtered.length === 0 ? (
              <Text color="light" css={panelEmptyStyle}>
                No locations match your search.
              </Text>
            ) : (
              filtered.map(({ loc }) => <LocationRow key={loc.id} location={loc} />)
            )}
          </div>
        </>
      )}
    </div>
  );
};

type IndexedLocation = {
  loc: WhitelistedLocation;
  searchBlob: string;
};

type LocationRowProps = { location: WhitelistedLocation };

const LocationRow = React.memo(({ location }: LocationRowProps) => {
  const nameDisplay = location.name || location.id || 'Unnamed location';

  return (
    <div css={rowStyle}>
      <div css={rowMainStyle}>
        <div css={rowTitleStyle}>
          <Text as="span" weight="bold" size="medium">
            {nameDisplay}
          </Text>
          <StatusBadge whitelisted={location.whitelisted} />
        </div>
        {location.address && (
          <Text as="span" size="small" color="light">
            {location.address}
          </Text>
        )}
      </div>
      <div css={rowMetaStyle}>
        <Text as="span" size="small" color="light">
          ID: <b>{location.id}</b>
        </Text>
      </div>
    </div>
  );
});
LocationRow.displayName = 'LocationRow';

const StatusBadge = ({ whitelisted }: { whitelisted: boolean }) => (
  <span css={whitelisted ? badgeOnStyle : badgeOffStyle}>
    {whitelisted ? 'Whitelisted' : 'Not whitelisted'}
  </span>
);

// --- styles ---------------------------------------------------------------

const panelStyle = (theme: WeaveTheme) => css`
  margin-bottom: ${theme.spacing(2)};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.colors.white};
  overflow: hidden;
`;

const panelHeaderStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(1.5, 2)};
  background: ${theme.colors.gray[100]};
  border-bottom: 1px solid ${theme.colors.gray[300]};
`;

const panelTitleStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing(1.5)};
`;

const panelSearchStyle = (theme: WeaveTheme) => css`
  padding: ${theme.spacing(1.5, 2)};
  border-bottom: 1px solid ${theme.colors.gray[200]};
`;

const panelListStyle = (theme: WeaveTheme) => css`
  max-height: 360px;
  overflow-y: auto;
  padding: ${theme.spacing(0.5, 0)};
`;

const panelLoadingStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  padding: ${theme.spacing(2)};
`;

const panelEmptyStyle = (theme: WeaveTheme) => css`
  display: block;
  padding: ${theme.spacing(2)};
  text-align: center;
`;

const rowStyle = (theme: WeaveTheme) => css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(1, 2)};
  border-bottom: 1px solid ${theme.colors.gray[200]};

  &:last-of-type {
    border-bottom: none;
  }
`;

const rowMainStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const rowTitleStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-wrap: wrap;
`;

const rowMetaStyle = (theme: WeaveTheme) => css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing(0.25)};
  flex-shrink: 0;
`;

const baseBadgeStyle = (theme: WeaveTheme) => css`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing(0.25, 1)};
  border-radius: ${theme.borderRadius.small};
  font-size: 12px;
  font-weight: ${theme.font.weight.bold};
  line-height: 1;
`;

const badgeOnStyle = (theme: WeaveTheme) => css`
  ${baseBadgeStyle(theme)};
  background: ${theme.colors.success ?? '#2bd53c'};
  color: ${theme.colors.white};
`;

const badgeOffStyle = (theme: WeaveTheme) => css`
  ${baseBadgeStyle(theme)};
  background: ${theme.colors.gray[200]};
  color: ${theme.colors.gray[600]};
`;
